import type {
  AddTicketRequester,
  BulkUpdateTicketsResult,
  CreateTicket,
  PaginatedResponse,
  SetTicketSourceDefinitions,
  Ticket,
  TicketAlertSubscriptionMap,
  TicketDraft,
  TicketDraftListItem,
  TicketFilters,
  TicketReference,
  TicketRequester,
  TicketSourceDefinition,
  UpdateTicket,
  UpsertTicketDraft,
} from '@scrambl/shared'

import { apiClient, emptyResponseToNull } from './api-client'
import { buildQueryString } from './base-api'

/** A file on the ticket's conversation, offered for linking onto a task. */
export interface TicketConversationAttachment {
  id: string
  fileName: string
  mimeType: string
  fileSize: number
  createdAt: string
  communicationId: string
  communicationSubject: string | null
  communicationDirection: 'INBOUND' | 'OUTBOUND' | null
  communicationSentAt: string | null
}

export interface TicketApprovalEntry {
  id: string
  action:
    | 'SUBMITTED'
    | 'APPROVED'
    | 'REJECTED'
    | 'CHANGES_REQUESTED'
    | 'RECALLED'
  previousStatus: string | null
  newStatus: string | null
  performedBy: string
  reason: string | null
  performedAt: string
  performer?: {
    id: string
    firstName: string | null
    lastName: string | null
    displayName: string | null
    email: string
    image: string | null
  } | null
}

export interface TicketRequesterWithRelations extends TicketRequester {
  contact?: {
    id: string
    firstName: string
    lastName: string
    email: string | null
    imageKey: string | null
  } | null
  entity?: { id: string; name: string; logoUrl: string | null } | null
  user?: {
    id: string
    firstName: string | null
    lastName: string | null
    displayName: string | null
    email: string
    image: string | null
  } | null
}

function cleanParams(params: Record<string, unknown>): Record<string, string> {
  const cleaned: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length > 0) cleaned[key] = value.join(',')
      continue
    }
    cleaned[key] = String(value)
  }
  return cleaned
}

export interface TicketCounts {
  total: number
  open: number
  assignedToMe: number
}

export interface MergeTicketsRequest {
  ticketIds: string[]
  primaryTicketId: string
  confirmation: 'MERGE'
}

export interface MergeTicketsResponse {
  primaryTicketId: string
  deletedTicketIds: string[]
  counts: Record<string, number>
}

// The current user's unsent reply composer for a ticket (server-synced so it
// follows the user across devices). replyDraftId scopes staged attachment
// uploads and is persisted with the attachments it validates.
export interface TicketReplyDraftPayload {
  bodyHtml: string
  ccText: string
  replyDraftId: string
  attachments: {
    storageKey: string
    fileName: string
    fileSize: number
    mimeType: string
  }[]
}

export interface TicketReplyDraftResponse extends TicketReplyDraftPayload {
  ticketId: string
  updatedAt: string
}

export const ticketsApi = {
  getTickets: async (
    params: TicketFilters = {},
  ): Promise<PaginatedResponse<Ticket>> => {
    const queryString = buildQueryString(
      cleanParams(params as Record<string, unknown>),
    )
    return apiClient<PaginatedResponse<Ticket>>(`/tickets${queryString}`)
  },

  getCounts: async (): Promise<TicketCounts> =>
    apiClient<TicketCounts>('/tickets/counts'),

  // Sidebar unread badge: tickets relevant to the viewer (assigned,
  // following, inbox notify member, or source alert subscriber) that they
  // haven't opened since the last activity.
  getUnreadCount: async (): Promise<{ count: number }> =>
    apiClient<{ count: number }>('/tickets/unread-count'),

  // Ids of the viewer's unread tickets plus their state, for the per-row
  // indicator in the list. `statuses` maps each id to 'new' (never opened) or
  // 'updated' (opened, then new activity). Same definition as the badge above.
  getUnreadIds: async (): Promise<{
    ids: string[]
    statuses: Record<string, 'new' | 'updated'>
  }> =>
    apiClient<{ ids: string[]; statuses: Record<string, 'new' | 'updated'> }>(
      '/tickets/unread-ids',
    ),

  markRead: async (id: string): Promise<{ ok: true }> =>
    apiClient<{ ok: true }>(`/tickets/${id}/read`, { method: 'POST' }),

  markAllRead: async (): Promise<{ updated: number }> =>
    apiClient<{ updated: number }>('/tickets/read-all', { method: 'POST' }),

  // Bulk read-state actions for the list toolbar. Read state is per-viewer
  // (receipts), so these never touch ticket data.
  markReadBulk: async (ticketIds: string[]): Promise<{ updated: number }> =>
    apiClient<{ updated: number }>('/tickets/read-bulk', {
      method: 'POST',
      body: { ticketIds },
    }),

  markUnreadBulk: async (ticketIds: string[]): Promise<{ updated: number }> =>
    apiClient<{ updated: number }>('/tickets/unread-bulk', {
      method: 'POST',
      body: { ticketIds },
    }),

  // Org-level "alert these users on new tickets from this source" config
  // (Tickets settings → Alerts). Map of source → user ids.
  getAlertSubscriptions: async (): Promise<TicketAlertSubscriptionMap> =>
    apiClient<TicketAlertSubscriptionMap>('/tickets/alert-subscriptions'),

  setAlertSubscriptions: async (
    data: TicketAlertSubscriptionMap,
  ): Promise<TicketAlertSubscriptionMap> =>
    apiClient<TicketAlertSubscriptionMap>('/tickets/alert-subscriptions', {
      method: 'PUT',
      body: data,
    }),

  // Self-service: toggle only the caller's own subscription for one source
  // (e.g. the form builder's "Notify me" hint). No admin rights needed.
  setSelfAlertSubscription: async (
    source: string,
    subscribed: boolean,
  ): Promise<{ source: string; subscribed: boolean }> =>
    apiClient<{ source: string; subscribed: boolean }>(
      '/tickets/alert-subscriptions/self',
      { method: 'PUT', body: { source, subscribed } },
    ),

  getSources: async (): Promise<TicketSourceDefinition[]> =>
    apiClient<TicketSourceDefinition[]>('/tickets/sources'),

  setSources: async (
    data: SetTicketSourceDefinitions,
  ): Promise<TicketSourceDefinition[]> =>
    apiClient<TicketSourceDefinition[]>('/tickets/sources', {
      method: 'PUT',
      body: data,
    }),

  getTicket: async (id: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}`),

  getTicketByNumber: async (ticketNumber: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/by-number/${encodeURIComponent(ticketNumber)}`),

  createTicket: async (data: CreateTicket): Promise<Ticket> =>
    apiClient<Ticket>('/tickets', {
      method: 'POST',
      body: data,
    }),

  updateTicket: async (id: string, data: UpdateTicket): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}`, {
      method: 'PATCH',
      body: data,
    }),

  // ----- Snooze -----
  snoozeTicket: async (id: string, until: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/snooze`, {
      method: 'POST',
      body: { until },
    }),

  unsnoozeTicket: async (id: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/snooze`, {
      method: 'DELETE',
    }),

  // ----- Drafts -----
  listDrafts: async (): Promise<TicketDraftListItem[]> =>
    apiClient<TicketDraftListItem[]>('/tickets/drafts'),

  getDraft: async (id: string): Promise<TicketDraft> =>
    apiClient<TicketDraft>(`/tickets/drafts/${id}`),

  upsertDraft: async (
    id: string,
    body: UpsertTicketDraft,
  ): Promise<TicketDraft> =>
    apiClient<TicketDraft>(`/tickets/drafts/${id}`, {
      method: 'PUT',
      body,
    }),

  deleteDraft: async (id: string): Promise<{ ok: true }> =>
    apiClient<{ ok: true }>(`/tickets/drafts/${id}`, { method: 'DELETE' }),

  submitDraft: async (id: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/drafts/${id}/submit`, { method: 'POST' }),

  bulkUpdateTickets: async (
    ticketIds: string[],
    updates: UpdateTicket,
  ): Promise<BulkUpdateTicketsResult> => {
    const result = await apiClient<BulkUpdateTicketsResult | Ticket[]>(
      '/tickets/bulk/update',
      {
        method: 'POST',
        body: { ticketIds, updates, includeResult: true },
      },
    )
    if (!Array.isArray(result)) return result

    // During a rolling deployment an older API can still return an array.
    // Count only confirmed updates and report every omitted id as a failure.
    const requestedIds = [...new Set(ticketIds)]
    const updatedIds = new Set(result.map(ticket => ticket.id))
    const succeededIds = requestedIds.filter(id => updatedIds.has(id))
    const failures = requestedIds
      .filter(id => !updatedIds.has(id))
      .map(id => ({ id, reason: 'The server did not confirm this update' }))
    return {
      requested: requestedIds.length,
      succeeded: succeededIds.length,
      failed: failures.length,
      succeededIds,
      failures,
      tickets: result,
    }
  },

  deleteTicket: async (id: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}`, { method: 'DELETE' }),

  getRecycleBin: async (
    params: TicketFilters = {},
  ): Promise<PaginatedResponse<Ticket>> => {
    const queryString = buildQueryString(
      cleanParams(params as Record<string, unknown>),
    )
    return apiClient<PaginatedResponse<Ticket>>(
      `/tickets/recycle-bin${queryString}`,
    )
  },

  restoreTicket: async (id: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/recycle-bin/${id}/restore`, {
      method: 'POST',
    }),

  addAssignments: async (id: string, userIds: string[]): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/assignments`, {
      method: 'POST',
      body: { userIds },
    }),

  removeAssignment: async (id: string, userId: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/assignments/${userId}`, {
      method: 'DELETE',
    }),

  addFollowers: async (id: string, userIds: string[]): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/followers`, {
      method: 'POST',
      body: { userIds },
    }),

  removeFollower: async (id: string, userId: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/followers/${userId}`, {
      method: 'DELETE',
    }),

  addReferences: async (
    id: string,
    references: TicketReference[],
  ): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/references`, {
      method: 'POST',
      body: { references },
    }),

  removeReference: async (id: string, referenceId: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/references/${referenceId}`, {
      method: 'DELETE',
    }),

  getApprovalHistory: async (id: string): Promise<TicketApprovalEntry[]> =>
    apiClient<TicketApprovalEntry[]>(`/tickets/${id}/approval/history`),

  approve: async (id: string, reason?: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/approval/approve`, {
      method: 'POST',
      body: { reason },
    }),

  reject: async (id: string, reason?: string): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/approval/reject`, {
      method: 'POST',
      body: { reason },
    }),

  requestApprovalChanges: async (
    id: string,
    reason?: string,
  ): Promise<Ticket> =>
    apiClient<Ticket>(`/tickets/${id}/approval/request-changes`, {
      method: 'POST',
      body: { reason },
    }),

  promoteToTask: async (
    id: string,
    body: {
      projectId?: string | null
      groupId?: string | null
      sprintId?: string | null
      cycleInstanceId?: string | null
      bucketId?: string | null
      name?: string
      description?: string
      assigneeIds?: string[]
      priority?: string
      status?: string
      taskTypeDefId?: string | null
      dueDate?: string | null
      attachmentIds?: string[]
    },
  ): Promise<{ task: { id: string; name?: string }; ticket: Ticket }> =>
    apiClient(`/tickets/${id}/promote-to-task`, { method: 'POST', body }),

  // Conversation files offered by the promote dialog's per-task picker. The
  // ids come straight back as each task draft's `attachmentIds`.
  listAttachments: async (
    id: string,
  ): Promise<TicketConversationAttachment[]> =>
    apiClient(`/tickets/${id}/attachments`),

  splitToTasks: async (
    id: string,
    body: {
      tasks: Array<{
        name: string
        description?: string
        priority?: string
        attachmentIds?: string[]
      }>
      projectId?: string | null
      groupId?: string | null
      sprintId?: string | null
      cycleInstanceId?: string | null
      bucketId?: string | null
      assigneeIds?: string[]
      priority?: string
      status?: string
      taskTypeDefId?: string | null
      dueDate?: string | null
    },
  ): Promise<{
    tasks: { id: string; name?: string }[]
    ticket: Ticket
  }> => apiClient(`/tickets/${id}/split-to-tasks`, { method: 'POST', body }),

  // Bulk version of promoteToTask: each selected ticket becomes its own task
  // (name = ticket subject) in the shared destination, linked back to the
  // ticket. Tickets that can't be promoted are reported in `skipped`.
  bulkPromoteToTasks: async (
    ticketIds: string[],
    body: {
      projectId?: string | null
      groupId?: string | null
      sprintId?: string | null
      cycleInstanceId?: string | null
      bucketId?: string | null
      assigneeIds?: string[]
      priority?: string
      status?: string
      taskTypeDefId?: string | null
      matchTaskTypeToTicketType?: boolean
      dueDate?: string | null
    },
  ): Promise<{
    created: { ticketId: string; taskId: string }[]
    skipped: { ticketId: string; reason: string }[]
  }> =>
    apiClient('/tickets/bulk/promote-to-task', {
      method: 'POST',
      body: { ticketIds, ...body },
    }),

  getRequesters: async (id: string): Promise<TicketRequesterWithRelations[]> =>
    apiClient<TicketRequesterWithRelations[]>(`/tickets/${id}/requesters`),

  addRequester: async (
    id: string,
    body: AddTicketRequester,
  ): Promise<TicketRequesterWithRelations[]> =>
    apiClient<TicketRequesterWithRelations[]>(`/tickets/${id}/requesters`, {
      method: 'POST',
      body,
    }),

  removeRequester: async (
    id: string,
    requesterId: string,
  ): Promise<TicketRequesterWithRelations[]> =>
    apiClient<TicketRequesterWithRelations[]>(
      `/tickets/${id}/requesters/${requesterId}`,
      { method: 'DELETE' },
    ),

  /**
   * Send an email reply on a ticket. Backend handles RFC 5322 threading
   * (Message-ID / In-Reply-To / References) and persists the result as an
   * OUTBOUND Communication so the conversation timeline updates.
   */
  sendReply: async (
    id: string,
    data: {
      bodyText: string
      bodyHtml?: string
      toEmails?: string[]
      ccEmails?: string[]
      subject?: string
      replyDraftId?: string
      attachments?: {
        storageKey: string
        fileName: string
        fileSize: number
        mimeType: string
      }[]
    },
  ): Promise<{ communicationId: string; sesMessageId: string }> =>
    apiClient(`/tickets/${id}/reply`, {
      method: 'POST',
      body: data,
    }),

  /**
   * The current user's unsent reply composer for a ticket, synced
   * server-side so it can be resumed from any device. Saving an empty
   * draft deletes it; sending the reply deletes it server-side.
   */
  getReplyDraft: async (id: string): Promise<TicketReplyDraftResponse | null> =>
    emptyResponseToNull(
      await apiClient<TicketReplyDraftResponse | null>(
        `/tickets/${id}/reply-draft`,
      ),
    ),

  saveReplyDraft: async (
    id: string,
    data: TicketReplyDraftPayload,
  ): Promise<{ ok: true }> =>
    apiClient(`/tickets/${id}/reply-draft`, { method: 'PUT', body: data }),

  deleteReplyDraft: async (id: string): Promise<{ ok: true }> =>
    apiClient(`/tickets/${id}/reply-draft`, { method: 'DELETE' }),

  /**
   * Merge selected tickets into a primary. Records (communications,
   * assignments, followers, references, magic links) move to the primary
   * and the other tickets are soft-deleted.
   */
  mergeTickets: async (
    data: MergeTicketsRequest,
  ): Promise<MergeTicketsResponse> =>
    apiClient<MergeTicketsResponse>('/tickets/merge', {
      method: 'POST',
      body: data,
    }),
}
